import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Button, Input, Tabs, TabsList, TabsTrigger } from '@artstorefronts/ui';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { useImages } from '../context/image';
import { ImageDimensions } from '~/types';

interface DimensionsFormData {
  height?: number;
  width?: number;
  dimensionType?: 'inches' | 'centimeters';
}

interface CompactDimensionsInputProps {
  imageId: number;
  imageDimensions: ImageDimensions;
  setTriggerTimestamp?: (timestamp: number) => void;
}

function CompactDimensionsInput({ imageId, imageDimensions, setTriggerTimestamp }: CompactDimensionsInputProps) {
  const { t } = useTranslation('artwork');
  const { t: tc } = useTranslation('common');
  const { updateImage } = useImages();

  const { register, handleSubmit, reset, setValue, watch } = useForm<DimensionsFormData>({
    defaultValues: {
      height: imageDimensions.imageHeight,
      width: imageDimensions.imageWidth,
      dimensionType: imageDimensions.imageDimensionType || 'inches',
    },
  });

  const watchedHeight = watch('height');
  const watchedWidth = watch('width');
  const watchedDimensionType = watch('dimensionType');
  const hasChanges =
    watchedHeight !== imageDimensions.imageHeight ||
    watchedWidth !== imageDimensions.imageWidth ||
    watchedDimensionType !== (imageDimensions.imageDimensionType || 'inches');

  // Update form when imageDimensions change
  useEffect(() => {
    reset({
      height: imageDimensions.imageHeight,
      width: imageDimensions.imageWidth,
      dimensionType: imageDimensions.imageDimensionType || 'inches',
    });
  }, [imageDimensions, reset]);

  // Height and width are independent — each keeps exactly what was typed.
  // They used to be locked to the photo's pixel ratio, which made an exact
  // size like 18 × 24 in impossible to enter. See DimensionsInput.

  // Handle form submission
  const onSubmit = useCallback(
    async (values: DimensionsFormData) => {
      try {
        await updateImage(String(imageId), {
          height: values.height,
          width: values.width,
          dimensionType: values.dimensionType,
        });

        toast.success(t('dimensions.success.updated'));

        // Trigger content refresh
        if (setTriggerTimestamp) {
          setTriggerTimestamp(Date.now());
        }
      } catch (error) {
        console.error('Error updating dimensions:', error);
        toast.error(t('dimensions.error.update-failed'));
      }
    },
    [imageId, updateImage, setTriggerTimestamp]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex items-center gap-2 rounded-md">
      <div className="flex items-center gap-1">
        <Input
          {...register('height', {
            setValueAs: (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
            validate: (value: any) => {
              if (value !== undefined && value !== null && value !== '' && !isNaN(value) && value <= 0) {
                return t('dimensions.validation.must-be-positive');
              }
              return true;
            },
          })}
          placeholder={t('dimensions.height')}
          className="max-w-20"
        />
        <span className="text-xs text-gray-400">×</span>
        <Input
          {...register('width', {
            setValueAs: (value) => (value === '' || value === null || value === undefined ? undefined : Number(value)),
            validate: (value: any) => {
              if (value !== undefined && value !== null && value !== '' && !isNaN(value) && value <= 0) {
                return t('dimensions.validation.must-be-positive');
              }
              return true;
            },
          })}
          placeholder={t('dimensions.width')}
          className="max-w-20"
        />
      </div>

      <div className="">
        <Tabs
          value={watchedDimensionType}
          onValueChange={(value) => {
            if (value) {
              setValue('dimensionType', value as 'inches' | 'centimeters');
            }
          }}
        >
          <TabsList className="grid w-22 grid-cols-2 bg-transparent">
            <TabsTrigger value="inches">{t('dimensions.unit.inches-abbr')}</TabsTrigger>
            <TabsTrigger value="centimeters">{t('dimensions.unit.centimeters-abbr')}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Button type="submit" className="flex items-center gap-2" disabled={!hasChanges}>
        <Save className="h-4 w-4" />
        {tc('save')}
      </Button>
    </form>
  );
}

export default CompactDimensionsInput;
